import React, { useState, useCallback, useMemo } from 'react';
import { Loading } from '@components/atoms/Loading';
import { Button } from '@components/atoms/Button';
import { Tab } from '@components/atoms/Tab';
import { WarehouseLayout } from '@components/layout/WarehouseLayout';
import { getCategoryIcon } from '@utils/categoryIcons';
import { CategoryFormModal } from '@components/organisms/CategoryFormModal';
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';
import {
  useMaterialCategories,
  useCreateMaterialCategory,
  useUpdateMaterialCategory,
  useDeleteMaterialCategory,
} from '@hooks/api/warehouse';
import {
  MaterialCategory,
  MaterialCategoryCreateInput,
  MaterialCategoryUpdateInput,
  CategoryType,
} from '@models/warehouse.model';
import AddIcon from '@mui/icons-material/Add';
import CategoryIcon from '@mui/icons-material/Category';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import PublicIcon from '@mui/icons-material/Public';
import { toast } from 'react-toastify';
import { useTranslation } from '@hooks/useTranslation';

type FilterTab = 'material' | 'equipment';

const COLOR_MAP: Record<string, string> = {
  blue: 'bg-blue-100 text-blue-600',
  purple: 'bg-purple-100 text-purple-600',
  orange: 'bg-orange-100 text-orange-600',
  cyan: 'bg-cyan-100 text-cyan-600',
  yellow: 'bg-yellow-100 text-yellow-600',
  green: 'bg-green-100 text-green-600',
  indigo: 'bg-indigo-100 text-indigo-600',
};

const WarehouseCategoriesPage: React.FC = () => {
  const tActionMessages = useTranslation('actionMessages');
  const [activeTab, setActiveTab] = useState<FilterTab>('material');
  const [searchQuery, setSearchQuery] = useState('');
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<MaterialCategory | null>(null);
  const [categoryToDelete, setCategoryToDelete] = useState<MaterialCategory | null>(null);

  // API Hooks
  const {
    data: categories = [],
    isLoading: categoriesLoading,
    isError: categoriesError,
  } = useMaterialCategories();

  // Mutations
  const createMutation = useCreateMaterialCategory();
  const updateMutation = useUpdateMaterialCategory();
  const deleteMutation = useDeleteMaterialCategory();

  const isLoading = categoriesLoading && !categoriesError;

  // Filter categories by tab and search
  const filteredCategories = useMemo(() => {
    let filtered = categories;

    if (activeTab === 'material') {
      filtered = filtered.filter(
        (c) => c.type === CategoryType.MATERIAL || c.type === CategoryType.BOTH
      );
    } else {
      filtered = filtered.filter(
        (c) => c.type === CategoryType.EQUIPMENT || c.type === CategoryType.BOTH
      );
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          c.name.toLowerCase().includes(query) ||
          c.description?.toLowerCase().includes(query)
      );
    }

    return filtered.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  }, [categories, activeTab, searchQuery]);

  // Stats
  const materialCount = categories.filter(
    (c) => c.type === CategoryType.MATERIAL || c.type === CategoryType.BOTH
  ).length;
  const equipmentCount = categories.filter(
    (c) => c.type === CategoryType.EQUIPMENT || c.type === CategoryType.BOTH
  ).length;
  const activeCount = categories.filter((c) => c.isActive).length;

  // Handlers
  const handleAddCategory = useCallback(() => {
    setSelectedCategory(null);
    setIsFormModalOpen(true);
  }, []);

  const handleEditCategory = useCallback((category: MaterialCategory) => {
    setSelectedCategory(category);
    setIsFormModalOpen(true);
  }, []);

  const handleCloseFormModal = useCallback(() => {
    setIsFormModalOpen(false);
    setSelectedCategory(null);
  }, []);

  const handleSubmitForm = useCallback(
    async (data: MaterialCategoryCreateInput | MaterialCategoryUpdateInput) => {
      try {
        if ('categoryId' in data && data.categoryId) {
          await updateMutation.mutateAsync(data as MaterialCategoryUpdateInput);
          toast.success(tActionMessages.categoryUpdatedSuccessfully);
        } else {
          await createMutation.mutateAsync(data as MaterialCategoryCreateInput);
          toast.success(tActionMessages.categoryCreatedSuccessfully);
        }
        handleCloseFormModal();
      } catch (error) {
        console.error('Error saving category:', error);
        toast.error(tActionMessages.failedToSaveCategory);
      }
    },
    [
      createMutation,
      updateMutation,
      handleCloseFormModal,
      tActionMessages.categoryCreatedSuccessfully,
      tActionMessages.categoryUpdatedSuccessfully,
      tActionMessages.failedToSaveCategory,
    ]
  );

  const handleDeleteCategory = useCallback((category: MaterialCategory) => {
    setCategoryToDelete(category);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!categoryToDelete) return;
    try {
      await deleteMutation.mutateAsync(categoryToDelete.categoryId);
      toast.success(tActionMessages.categoryDeletedSuccessfully);
      setCategoryToDelete(null);
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error(tActionMessages.failedToDeleteCategory);
    }
  }, [
    categoryToDelete,
    deleteMutation,
    tActionMessages.categoryDeletedSuccessfully,
    tActionMessages.failedToDeleteCategory,
  ]);

  const handleCancelDelete = useCallback(() => {
    setCategoryToDelete(null);
  }, []);

  if (isLoading) {
    return (
      <WarehouseLayout>
        <div className="flex justify-center items-center h-full">
          <Loading size="3xl" />
        </div>
      </WarehouseLayout>
    );
  }

  return (
    <WarehouseLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Categories
            </h1>
          </div>
          <Button
            variant="primary"
            leftIcon={<AddIcon sx={{ fontSize: 16 }} />}
            onClick={handleAddCategory}
          >
            Add Category
          </Button>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4 flex items-center gap-3">
            <CategoryIcon className="text-gray-400" sx={{ fontSize: 24 }} />
            <div>
              <p className="text-2xl font-bold text-gray-900">{categories.length}</p>
              <p className="text-xs text-gray-500">Total Categories</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4 flex items-center gap-3">
            <CheckCircleIcon className="text-green-500" sx={{ fontSize: 24 }} />
            <div>
              <p className="text-2xl font-bold text-gray-900">{activeCount}</p>
              <p className="text-xs text-gray-500">Active</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4 flex items-center gap-3">
            <CategoryIcon className="text-indigo-500" sx={{ fontSize: 24 }} />
            <div>
              <p className="text-2xl font-bold text-gray-900">{categories.length - activeCount}</p>
              <p className="text-xs text-gray-500">Inactive</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tab
          tabs={[
            { id: 'material', name: `Material (${materialCount})` },
            { id: 'equipment', name: `Equipment (${equipmentCount})` },
          ]}
          activeTab={activeTab}
          onTabChange={(tabId) => setActiveTab(tabId as FilterTab)}
          variant="pills"
        />

        {/* Search */}
        <div className="relative w-64">
          <SearchIcon
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            sx={{ fontSize: 18 }}
          />
          <input
            type="text"
            placeholder="Search categories..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full border border-gray-300 rounded-md py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Add Category Card */}
          <button type="button"
            onClick={handleAddCategory}
            className="border-2 border-dashed border-gray-300 rounded-lg p-4 flex flex-col items-center justify-center gap-2 text-gray-400 hover:text-indigo-600 hover:border-indigo-300 transition-colors min-h-[140px]"
          >
            <AddIcon sx={{ fontSize: 24 }} />
            <span className="text-sm font-medium">Add Category</span>
          </button>

          {filteredCategories.map((cat) => {
            const colorClasses = COLOR_MAP[cat.color || 'blue'] || COLOR_MAP.blue;

            return (
              <div
                key={cat.categoryId}
                className={`bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4 relative group transition-all hover:shadow-md ${
                  !cat.isActive ? 'opacity-60' : ''
                }`}
              >
                {/* Icon */}
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${colorClasses}`}>
                  {getCategoryIcon(cat.icon, { sx: { fontSize: 22 } })}
                </div>

                {/* Name & Description */}
                <h3 className="text-sm font-semibold text-gray-900">{cat.name}</h3>
                {cat.description && (
                  <p className="text-xs text-gray-500 mt-1 line-clamp-2">{cat.description}</p>
                )}

                {/* Status & Type */}
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      cat.isActive
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {cat.isActive ? 'Active' : 'Inactive'}
                  </span>
                  {cat.isGlobal && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                      <PublicIcon sx={{ fontSize: 12 }} />
                      Global
                    </span>
                  )}
                  <span className="text-xs text-gray-400">
                    {cat.type === CategoryType.BOTH ? 'Material & Equipment' : cat.type}
                  </span>
                </div>

                {/* Hover Actions (hidden for global categories) */}
                {!cat.isGlobal && (
                  <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                    <button type="button"
                      onClick={() => handleEditCategory(cat)}
                      className="p-1.5 bg-white rounded-md shadow-sm ring-1 ring-gray-200 text-gray-400 hover:text-indigo-600 hover:ring-indigo-200"
                      title="Edit"
                    >
                      <EditIcon sx={{ fontSize: 16 }} />
                    </button>
                    <button type="button"
                      onClick={() => handleDeleteCategory(cat)}
                      className="p-1.5 bg-white rounded-md shadow-sm ring-1 ring-gray-200 text-gray-400 hover:text-red-600 hover:ring-red-200"
                      title="Delete"
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}

        </div>
      </div>

      {/* Form Modal */}
      <CategoryFormModal
        isOpen={isFormModalOpen}
        onClose={handleCloseFormModal}
        onSubmit={handleSubmitForm}
        category={selectedCategory}
        isLoading={createMutation.isPending || updateMutation.isPending}
        defaultType={activeTab === 'equipment' ? CategoryType.EQUIPMENT : CategoryType.MATERIAL}
      />

      {/* Delete Confirmation */}
      <ConfirmationModal
        isOpen={!!categoryToDelete}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Category"
        description={`Are you sure you want to delete "${categoryToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />
    </WarehouseLayout>
  );
};

export default WarehouseCategoriesPage;
